"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeMonthlyGeneration = computeMonthlyGeneration;
exports.deriveFederalStatusFromGeneration = deriveFederalStatusFromGeneration;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
/** UTC first-of-month for `month`. Matches the `@db.Date` periodStart storage. */
function monthStartUtc(month) {
    return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1));
}
/** UTC first-of-the-next-month — the exclusive end of the month window. */
function nextMonthStartUtc(month) {
    return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1));
}
/**
 * Sums a facility's positive `ContainerFillEvent` mass for one calendar month,
 * split into RCRA acute vs non-acute by the container's `WasteStream`
 * classification.
 *
 * A positive `ContainerFillEvent` ADD *is* a generation event (Project 1
 * already normalized it to `canonicalKg`), so generation is measured from the
 * ADDs directly rather than from shipments — which lag by the accumulation
 * time between generating waste and shipping it.
 *
 * Reportability mirrors the shipment aggregation's filter: `willNotReport`
 * streams and `NONHAZARDOUS` are excluded. `STATE_REGULATED` does not feed the
 * federal number either (federal-only in 8A). Volume-only ADDs on a
 * federally-counted stream (`canonicalKg` null) are surfaced as incomplete
 * data rather than counted as zero.
 *
 * `fills` and `streams` must already be scoped to the target facility; the
 * caller owns that scoping (the facility never appears in the arithmetic).
 */
function computeMonthlyGeneration(month, fills, streams) {
    const start = monthStartUtc(month).getTime();
    const end = nextMonthStartUtc(month).getTime();
    const streamsById = new Map(streams.map((stream) => [stream.id, stream]));
    const result = {
        hazardousKg: 0,
        acuteKg: 0,
        fillEventCount: 0,
        incompleteFills: [],
    };
    for (const fill of fills) {
        // Only positive ADDs are generation; removals/transfers/empties are not.
        if (fill.amount <= 0)
            continue;
        // A consolidation transfer-in re-adds already-counted waste on the
        // receiving container — exclude it so consolidation can't inflate the month.
        if (fill.sourceContainerId !== null)
            continue;
        const occurred = fill.occurredAt.getTime();
        if (occurred < start || occurred >= end)
            continue;
        // No stream → classification unknown, so it can't be attributed to the
        // federal split. A container without an assigned stream is a data gap the
        // stream-surfacing work (8C) owns, not the generation number.
        if (fill.wasteStreamId === null)
            continue;
        const stream = streamsById.get(fill.wasteStreamId);
        if (!stream)
            continue;
        // Reportability + waste-kind boundary, matching the shipment aggregation.
        if (stream.willNotReport)
            continue;
        const { classification } = stream;
        if (classification !== browser_1.WasteClassification.RCRA_HAZARDOUS &&
            classification !== browser_1.WasteClassification.RCRA_ACUTELY_HAZARDOUS) {
            // NONHAZARDOUS and STATE_REGULATED do not count toward federal
            // generation. (State-driven generation status is out of 8A scope.)
            continue;
        }
        if (fill.canonicalKg === null) {
            // Federally-counted ADD whose mass we can't sum — record, don't zero.
            result.incompleteFills.push({
                occurredAt: fill.occurredAt,
                wasteStreamId: fill.wasteStreamId,
                classification,
            });
            continue;
        }
        // canonicalKg is signed; an ADD's mass is positive. Guard defensively so a
        // sign mismatch can't subtract from the monthly total.
        if (fill.canonicalKg <= 0)
            continue;
        if (classification === browser_1.WasteClassification.RCRA_ACUTELY_HAZARDOUS) {
            result.acuteKg += fill.canonicalKg;
        }
        else {
            result.hazardousKg += fill.canonicalKg;
        }
        result.fillEventCount += 1;
    }
    return result;
}
/**
 * Federal RCRA generator status for one month's generation, in kilograms.
 *
 * This is the kilogram-native twin of the WADM chart's `calculateMonthStatus`
 * (`apps/api/.../generationByMonth.ts`): same branch order, same thresholds —
 * but working in the kg that container tracking records (`canonicalKg`) instead
 * of the lb the chart uses, and returning the Prisma `FederalRegulatoryStatus`
 * that `CalculatedRegulatoryStatus` stores instead of the GraphQL enum. It
 * reuses the shared `wasteGeneratorStatusThresholds` (kg variants) verbatim, so
 * no threshold logic is duplicated.
 *
 * Returns null when nothing was generated — the month then gets no calculated
 * row, matching the shipment path (which also yields no status for an
 * empty month rather than NOT_A_GENERATOR).
 */
function deriveFederalStatusFromGeneration(generation) {
    const { hazardousKg, acuteKg } = generation;
    if (hazardousKg <= 0 && acuteKg <= 0)
        return null;
    if (acuteKg > wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_ACUTE_HAZARDOUS_KG) {
        return browser_1.FederalRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if (hazardousKg >= wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_HAZARDOUS_KG) {
        return browser_1.FederalRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if (hazardousKg >= wasteGeneratorStatusThresholds_1.SQG_THRESHOLD_HAZARDOUS_KG) {
        return browser_1.FederalRegulatoryStatus.SMALL_QUANTITY_GENERATOR;
    }
    return browser_1.FederalRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR;
}
//# sourceMappingURL=generation.js.map